/**
 * Checklist for Spain / Thailand provinces; syncs with newline-separated countyText.
 */
import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  normalizeGtgTargetCountry,
  getGtgCanonicalSubdivisionList,
  parseSubdivisionsForTargetCountry,
  orderedSubdivisionsFromSelectedSet,
  GTG_TARGET_COUNTRY_ES,
  GTG_TARGET_COUNTRY_TH,
} from "@healthdesk/shared-utils";

/**
 * @param {{
 *   targetCountry: string,
 *   value: string,
 *   onChange: (next: string) => void,
 *   disabled?: boolean,
 * }} props
 */
const GtgSubdivisionChecklistPicker = ({ targetCountry, value, onChange, disabled = false }) => {
  const tc = normalizeGtgTargetCountry(targetCountry);
  const [filter, setFilter] = useState("");
  const filterLower = filter.trim().toLowerCase();
  const orderedList = useMemo(() => [...getGtgCanonicalSubdivisionList(tc)], [tc]);

  const selectedSet = useMemo(() => {
    const { counties } = parseSubdivisionsForTargetCountry(value || "", tc);
    return new Set(counties);
  }, [value, tc]);

  const visible = useMemo(() => {
    if (!filterLower) return orderedList;
    return orderedList.filter((name) => name.toLowerCase().includes(filterLower));
  }, [orderedList, filterLower]);

  const toggle = useCallback(
    (canonName, checked) => {
      const next = new Set(selectedSet);
      if (checked) next.add(canonName);
      else next.delete(canonName);
      const ordered = orderedSubdivisionsFromSelectedSet(next, tc);
      onChange(ordered.join("\n"));
    },
    [selectedSet, onChange, tc]
  );

  const noun = tc === GTG_TARGET_COUNTRY_ES || tc === GTG_TARGET_COUNTRY_TH ? "provinces" : "items";
  const title =
    tc === GTG_TARGET_COUNTRY_ES
      ? "Select Spain provinces"
      : tc === GTG_TARGET_COUNTRY_TH
        ? "Select Thailand provinces"
        : "Select subdivisions";

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <TextField
        size="small"
        fullWidth
        label={`Filter ${noun}`}
        placeholder="Type to narrow the list"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        disabled={disabled}
        sx={{ mb: 1.5 }}
      />
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {selectedSet.size} selected · canonical names match the list above
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          maxHeight: 280,
          overflow: "auto",
          px: 1,
          py: 0.5,
        }}
      >
        <FormGroup>
          {visible.map((name) => (
            <FormControlLabel
              key={name}
              control={
                <Checkbox
                  size="small"
                  checked={selectedSet.has(name)}
                  onChange={(e) => toggle(name, e.target.checked)}
                  disabled={disabled}
                />
              }
              label={name}
              sx={{ mr: 0, alignItems: "flex-start", "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
            />
          ))}
        </FormGroup>
        {visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 1 }}>
            No {noun} match this filter.
          </Typography>
        ) : null}
      </Paper>
    </Box>
  );
};

export default GtgSubdivisionChecklistPicker;
